<template>
  <div class="text-center">
    <v-dialog v-model="newServiceTypeDialog.visibility" max-width="620" persistent scrollable>
      <v-card class="pa-0" flat>
        <!-- Header -->
        <v-toolbar dark dense color="rgb(47, 207, 156)">
          <v-btn icon disabled></v-btn>
          <v-spacer></v-spacer>
          <v-toolbar-title>New Queue</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon dark @click="close">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>

        <v-card-text class="pt-6">
          <v-row no-gutters>
            <!-- Service Type -->
            <v-col cols="12" sm="8" class="pb-4 pr-sm-2">
              <v-text-field v-model="form.service_type" outlined dense hide-details="auto" label="Service Type" hint="Internal queue identifier" persistent-hint></v-text-field>
            </v-col>

            <!-- Prefix -->
            <v-col cols="12" sm="4" class="pb-4 pl-sm-2">
              <v-text-field v-model="form.prefix" outlined dense hide-details="auto" label="Prefix" :hint="prefixHint" persistent-hint></v-text-field>
            </v-col>

            <!-- Display Names -->
            <v-col cols="12" sm="4" class="pb-4 pr-sm-2">
              <v-text-field v-model="form.name" outlined dense hide-details label="Chinese (Traditional)"></v-text-field>
            </v-col>
            <v-col cols="12" sm="4" class="pb-4 px-sm-2">
              <v-text-field v-model="form.name_cn" outlined dense hide-details label="Chinese (Simplified)"></v-text-field>
            </v-col>
            <v-col cols="12" sm="4" class="pb-4 pl-sm-2">
              <v-text-field v-model="form.name_en" outlined dense hide-details label="English"></v-text-field>
            </v-col>

            <!-- Numbering -->
            <v-col cols="12" sm="4" class="pb-4 pr-sm-2">
              <v-text-field v-model="form.number_start" type="number" min="1" outlined dense hide-details label="Number Start"></v-text-field>
            </v-col>
            <v-col cols="12" sm="4" class="pb-4 px-sm-2">
              <v-text-field v-model="form.number_padding" type="number" min="1" max="10" outlined dense hide-details label="Number Padding"></v-text-field>
            </v-col>
            <v-col cols="12" sm="4" class="pb-4 pl-sm-2">
              <v-text-field v-model="form.threshold" type="number" min="0" max="360" outlined dense hide-details label="Threshold (Mins)"></v-text-field>
            </v-col>

            <v-col cols="12" class="pb-2">
              <v-alert dense text type="info" class="text-body-2 mb-0"
                >First ticket of the day: <strong>{{ sampleCode }}</strong></v-alert
              >
            </v-col>

            <!-- Booking Enabled -->
            <v-col cols="12">
              <v-switch
                v-model="form.booking_enabled"
                dense
                hide-details
                class="mt-0"
                label="Online booking queue"
                hint="Booking queues only accept tickets created from an imported booking"
                persistent-hint
              ></v-switch>
            </v-col>
          </v-row>
        </v-card-text>

        <v-card-actions class="px-6 pb-6">
          <v-btn block depressed color="primary" :disabled="saving" @click="create">Create</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapActions, mapMutations, mapState } from 'vuex'
import { normalizePrefix, validatePrefix, PREFIX_FORMAT_MESSAGE } from '@/utils/prefix'
import { numericFieldValue } from '@/utils/form'

const MAX_PADDING = 10
// Matches the ceiling of the Threshold slider on Queue Setting, so a queue created here
// stays editable there.
const MAX_THRESHOLD_MINUTES = 360

function emptyForm() {
  return {
    service_type: null,
    prefix: null,
    name: null,
    name_cn: null,
    name_en: null,
    threshold: 15,
    number_start: 1,
    number_padding: 3,
    booking_enabled: false
  }
}

export default {
  name: 'newServiceTypeDialog',
  data() {
    return {
      form: emptyForm(),
      saving: false
    }
  },
  computed: {
    ...mapState(['newServiceTypeDialog', 'queueSetting']),
    prefixHint() {
      return PREFIX_FORMAT_MESSAGE
    },
    // Normalised once so the preview and the value actually submitted cannot disagree.
    normalizedPrefix() {
      return normalizePrefix(this.form.prefix)
    },
    threshold() {
      return numericFieldValue(this.form.threshold)
    },
    numberStart() {
      return numericFieldValue(this.form.number_start)
    },
    numberPadding() {
      return numericFieldValue(this.form.number_padding)
    },
    numberPaddingInRange() {
      return Number.isInteger(this.numberPadding) && this.numberPadding >= 1 && this.numberPadding <= MAX_PADDING
    },
    // Shows the business user exactly what the numbering settings produce.
    sampleCode() {
      if (!this.normalizedPrefix || !Number.isInteger(this.numberStart) || !this.numberPaddingInRange) return '-'
      return `${this.normalizedPrefix}${String(this.numberStart).padStart(this.numberPadding, '0')}`
    }
  },
  methods: {
    ...mapActions(['createServiceType', 'fetchServiceTypeList']),
    ...mapMutations(['SET_NEW_SERVICE_TYPE_DIALOG_VISIBILITY', 'SET_SNACKBAR']),
    error(text) {
      this.SET_SNACKBAR({ status: true, color: 'error', text, icon: 'close', timeout: '4000' })
    },
    validate() {
      if (!(this.form.service_type || '').trim()) return 'Service type is required.'

      const prefix_error = validatePrefix(this.normalizedPrefix, this.queueSetting.items)
      if (prefix_error) return prefix_error

      if (!(this.form.name || '').trim() || !(this.form.name_cn || '').trim() || !(this.form.name_en || '').trim()) {
        return 'All three display names (Traditional / Simplified / English) are required.'
      }

      if (!Number.isInteger(this.threshold) || this.threshold < 0 || this.threshold > MAX_THRESHOLD_MINUTES) {
        return `Threshold must be a whole number of minutes between 0 and ${MAX_THRESHOLD_MINUTES}.`
      }

      if (!Number.isInteger(this.numberStart) || this.numberStart < 1) return 'Number start must be an integer >= 1.'
      if (!this.numberPaddingInRange) return `Number padding must be an integer between 1 and ${MAX_PADDING}.`

      return null
    },
    async create() {
      const message = this.validate()
      if (message) return this.error(message)

      const params = {
        service_type: this.form.service_type.trim(),
        prefix: this.normalizedPrefix,
        name: this.form.name.trim(),
        name_cn: this.form.name_cn.trim(),
        name_en: this.form.name_en.trim(),
        threshold: this.threshold,
        number_start: this.numberStart,
        number_padding: this.numberPadding,
        booking_enabled: this.form.booking_enabled ? 1 : 0
      }

      this.saving = true
      // See bookingProfileDialog: the close is gated on the dialog session, the list refresh is
      // not — the queue was created either way and every screen reads that list.
      const session = this.newServiceTypeDialog.session
      try {
        if (await this.createServiceType({ params })) {
          this.fetchServiceTypeList()
          if (this.newServiceTypeDialog.session === session) this.close()
        }
      } finally {
        this.saving = false
      }
    },
    close() {
      this.SET_NEW_SERVICE_TYPE_DIALOG_VISIBILITY(false)
    }
  },
  watch: {
    'newServiceTypeDialog.visibility'(visible) {
      if (visible) this.form = emptyForm()
    }
  }
}
</script>
